import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import Select from "react-select";
import toast from "react-hot-toast";
import Pagination from "../components/Pagination";
import { useButtonTableStyle } from "../utils/useButtonTableStyle";
import { useIndiaStateCity } from "../utils/useIndiaStateCity";
import {
  sanitizeMobileInput,
  mobileInputFieldProps,
  getMobileValidationError,
} from "../utils/mobileInput";
import {
  emailInputFieldProps,
  getEmailValidationError,
} from "../utils/emailInput";
import { useMobileExistsCheck } from "../utils/useMobileExistsCheck";
import MobileExistsFeedback from "../components/MobileExistsFeedback";
import { hasPermission, PERMISSIONS } from "../utils/permissions";
import useIsCurrentFinancialYear, {
  FY_ADD_DISABLED_TITLE,
} from "../hooks/useIsCurrentFinancialYear";

import { createErpSocket } from "../utils/socketIo";
import {
  createCustomerUser,
  createCustomerUserMultipart,
  listCustomerUsers,
  updateCustomerUser,
  updateCustomerUserMultipart,
} from "../erpClient";
import DocNumberInput from "../components/DocNumberInput";
import {
  parseApiPagination,
  extractListRowsFromResponse,
} from "../utils/parseApiPagination";
import {
  useVisitedPageCache,
  visitedPageKey,
} from "../utils/visitedPageCache";
import {
  buildReferenceSelectOptions,
  customerSelectMenuProps,
  fetchCustomerUserSearch,
  toReferenceSelectOption,
} from "../utils/customerUserSelectOptions";
import { formatIndianRupee } from "../utils/formatLiveRate";

const ITEMS_PER_PAGE = 10;

const extractCustomerFromPayload = (data) => {
  if (Array.isArray(data)) {
    return extractCustomerFromPayload(data[0]);
  }
  return (
    data?.customer ??
    data?.user ??
    data?.customer_user ??
    data?.data?.customer ??
    data?.data?.user ??
    (Array.isArray(data?.data)
      ? extractCustomerFromPayload(data.data[0])
      : null) ??
    (data?.id != null && (data.name != null || data.mobile != null)
      ? data
      : null)
  );
};

const customSelectStyles = {
  control: (base) => ({
    ...base,
    borderColor: "#dee2e6",
    boxShadow: "none",
    "&:hover": {
      borderColor: "#dee2e6",
    },
  }),
  input: (base) => ({
    ...base,
    color: "#000",
  }),
  singleValue: (base) => ({
    ...base,
    color: "#000",
  }),
  menu: (base) => ({
    ...base,
    zIndex: 9999,
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999,
  }),
};

function CustomerTable() {
  const navigate = useNavigate();
  const isCurrentFinancialYear = useIsCurrentFinancialYear();
  const { buttonClass, tableClass, cardClass } = useButtonTableStyle();
  const [users, setUsers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [isLoading, setIsLoading] = useState(true);
  const [itemsPerPage, setItemsPerPage] = useState(ITEMS_PER_PAGE);
  const [totalCount, setTotalCount] = useState(0);
  const [canAddCustomer, setCanAddCustomer] = useState(() =>
    hasPermission(PERMISSIONS.CUSTOMER_ADD),
  );
  const [canEditCustomer, setCanEditCustomer] = useState(() =>
    hasPermission(PERMISSIONS.CUSTOMER_EDIT),
  );
  const [canSettlement, setCanSettlement] = useState(
    () =>
      hasPermission(PERMISSIONS.CUSTOMER_SETTLEMENT_VIEW) ||
      hasPermission(PERMISSIONS.CUSTOMER_SETTLEMENT_ADD) ||
      hasPermission(PERMISSIONS.CUSTOMER_SETTLEMENT_EDIT),
  );

  useEffect(() => {
    const syncPerms = () => {
      setCanAddCustomer(hasPermission(PERMISSIONS.CUSTOMER_ADD));
      setCanEditCustomer(hasPermission(PERMISSIONS.CUSTOMER_EDIT));
      setCanSettlement(
        hasPermission(PERMISSIONS.CUSTOMER_SETTLEMENT_VIEW) ||
        hasPermission(PERMISSIONS.CUSTOMER_SETTLEMENT_ADD) ||
        hasPermission(PERMISSIONS.CUSTOMER_SETTLEMENT_EDIT),
      );
    };
    window.addEventListener("permissionsUpdated", syncPerms);
    window.addEventListener("erpAuthUpdated", syncPerms);
    return () => {
      window.removeEventListener("permissionsUpdated", syncPerms);
      window.removeEventListener("erpAuthUpdated", syncPerms);
    };
  }, []);

  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // Add Customer form states
  const [fullName, setFullName] = useState("");
  const [mobile, setMobile] = useState("");
  const [email, setEmail] = useState("");
  const [state, setState] = useState("");
  const [city, setCity] = useState("");
  const [address, setAddress] = useState("");
  const [area, setArea] = useState("");
  const [aadharNo, setAadharNo] = useState("");
  const [panNo, setPanNo] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [maritalStatus, setMaritalStatus] = useState("");
  const [anniversaryDate, setAnniversaryDate] = useState("");
  const [balance, setBalance] = useState("");
  const [openingGoldBalance, setOpeningGoldBalance] = useState("");
  const [openingSilverBalance, setOpeningSilverBalance] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [companyAddress, setCompanyAddress] = useState("");
  const [gstNumber, setGstNumber] = useState("");
  const [aadharImage, setAadharImage] = useState(null);
  const [panImage, setPanImage] = useState(null);
  const [gstImage, setGstImage] = useState(null);
  const [religion, setReligion] = useState("");
  const [otherPhones, setOtherPhones] = useState("");
  const [referenceOption, setReferenceOption] = useState(null);
  const [referenceSearchInput, setReferenceSearchInput] = useState("");
  const [referenceOptions, setReferenceOptions] = useState([]);
  const [loadingReferenceSearch, setLoadingReferenceSearch] = useState(false);
  const referenceSearchTimerRef = useRef(null);
  const [editReferenceOption, setEditReferenceOption] = useState(null);
  const [editReferenceSearchInput, setEditReferenceSearchInput] = useState("");
  const [editReferenceOptions, setEditReferenceOptions] = useState([]);
  const [loadingEditReferenceSearch, setLoadingEditReferenceSearch] =
    useState(false);
  const editReferenceSearchTimerRef = useRef(null);
  /** 1 = active, 0 = inactive (API payload) */
  const [addCustomerStatus, setAddCustomerStatus] = useState(1);

  const {
    states,
    cities,
    selectedState,
    setSelectedState,
  } = useIndiaStateCity("GJ");
  const statesRef = useRef(states);
  statesRef.current = states;

  const addMobileExistsCheck = useMobileExistsCheck(mobile, {
    enabled: showAddModal,
    localUsers: users,
  });

  const editMobileExistsCheck = useMobileExistsCheck(
    selectedCustomer?.mobile ?? "",
    {
      enabled: showEditModal && Boolean(selectedCustomer),
      excludeCustomerId: selectedCustomer?.id ?? null,
      localUsers: users,
    },
  );

  const normalizeDate = (dateValue) => {
    const raw = String(dateValue || "").trim();
    if (!raw || raw === "0000-00-00") return null;
    const matched = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    return matched ? raw : null;
  };

  const toNumberOrDefault = (value, fallback = 0) => {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : fallback;
  };

  const createIdempotencyKey = () => {
    if (
      typeof crypto !== "undefined" &&
      typeof crypto.randomUUID === "function"
    ) {
      return crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  };

  const getSafeSuccessMessage = (apiMessage, fallbackMessage) => {
    const msg = String(apiMessage || "").trim();
    if (!msg) return fallbackMessage;
    if (msg.toLowerCase() === "radha") return fallbackMessage;
    return msg;
  };

  const mapCustomerToRow = useCallback((user, stateList = []) => {
    const stateObj = stateList.find(
      (s) => s.iso2 === user.state || s.name === user.state,
    );
    const stateName = stateObj ? stateObj.name : user.state;
    const finalBalanceValue = toNumberOrDefault(
      user.final_balance ?? user.finalBalance ?? user.balance ?? 0,
      0,
    );
    const balanceType = String(user.balance_type || "").toUpperCase();
    const isDue =
      balanceType === "DUE" ||
      (balanceType !== "ADVANCE" && finalBalanceValue < 0);
    return {
      id: user.id,
      fullName: user.name || user.full_name || "",
      mobile: user.mobile || "",
      email: user.email || "",
      state: stateName || "",
      city: user.city || "",
      address: user.address || "",
      area: user.area || "",
      aadhar_no: user.aadhar_no || "",
      pan_no: user.pan_no || "",
      aadhar_card: user.aadhar_card || "",
      pan_card: user.pan_card || "",
      gst_document: user.gst_document || "",
      birthDate: normalizeDate(user.birth_date),
      maritalStatus: user.marital_status || "",
      anniversaryDate: normalizeDate(user.anniversary_date),
      balance:
        user.users_balance ??
        user.balance ??
        user.final_balance ??
        user.finalBalance ??
        0,
      saleBalance: user.sale_balance ?? 0,
      finalBalance: Math.abs(finalBalanceValue),
      finalBalanceRaw: finalBalanceValue,
      balanceType: isDue ? "DUE" : "ADVANCE",
      companyName: user.company_name || "",
      companyAddress: user.company_address || "",
      GST_number: user.gst_number || user.GST_number || "",
      reference: user.reference || "",
      religion: user.religion || "",
      other_phones: user.other_phones || user.otherPhones || "",
      openingGoldBalance:
        user.gold_balance_gm ??
        user.gold_balance ??
        user.opening_gold_balance ??
        "",
      openingSilverBalance:
        user.silver_balance_gm ??
        user.silver_balance ??
        user.opening_silver_balance ??
        "",
      status: Number(user.status) === 0 ? 0 : 1,
    };
  }, []);

  const scheduleReferenceSearch = (rawInput, forEdit = false) => {
    const timerRef = forEdit
      ? editReferenceSearchTimerRef
      : referenceSearchTimerRef;
    const setSearchInput = forEdit
      ? setEditReferenceSearchInput
      : setReferenceSearchInput;
    const setOptions = forEdit ? setEditReferenceOptions : setReferenceOptions;
    const setLoading = forEdit
      ? setLoadingEditReferenceSearch
      : setLoadingReferenceSearch;

    clearTimeout(timerRef.current);
    setSearchInput(rawInput);
    if (!String(rawInput ?? "").trim()) {
      setOptions([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    timerRef.current = setTimeout(async () => {
      try {
        const rows = await fetchCustomerUserSearch(rawInput);
        const opts = buildReferenceSelectOptions(rows);
        setOptions(opts);
      } catch {
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, 400);
  };

  const resolveReferenceOption = useCallback(
    async (referenceId) => {
      const refId = String(referenceId ?? "").trim();
      if (!refId) return null;

      const local = users.find((u) => String(u.id) === refId);
      if (local) {
        return toReferenceSelectOption({
          id: local.id,
          name: local.fullName,
          mobile: local.mobile,
        });
      }

      try {
        const rows = await fetchCustomerUserSearch(refId);
        const match = rows.find((row) => String(row.id) === refId);
        return match ? toReferenceSelectOption(match) : null;
      } catch {
        return null;
      }
    },
    [users],
  );

  const upsertCustomerInTable = useCallback(
    (customerRaw, formRow = null) => {
      if (customerRaw?.id != null) {
        const row = mapCustomerToRow(customerRaw, statesRef.current);
        setUsers((prev) => {
          const exists = prev.some((user) => user.id === row.id);
          if (exists) {
            return prev.map((user) => (user.id === row.id ? row : user));
          }
          return [row, ...prev].sort((a, b) => b.id - a.id);
        });
        return;
      }

      if (formRow?.id != null) {
        setUsers((prev) =>
          prev.map((user) =>
            user.id === formRow.id ? { ...user, ...formRow } : user,
          ),
        );
      }
    },
    [mapCustomerToRow],
  );

  const stateOptions = states.map((state) => ({
    label: state.name,
    value: state.iso2,
  }));
  const cityOptions = cities.map((city) => ({
    label: city.name,
    value: city.name,
  }));

  const isFetching = useRef(false);
  const pageCache = useVisitedPageCache();

  const fetchUsers = useCallback(async (page = 1, limit = 10, search = "", force = false) => {
    const cacheKey = visitedPageKey([page, limit, String(search || "").trim()]);
    if (force) pageCache.clear();
    const cached = force ? null : pageCache.read(cacheKey);
    if (cached) {
      setUsers(cached.items);
      setTotalCount(cached.total);
      setIsLoading(false);
      return;
    }
    if (isFetching.current) return;
    isFetching.current = true;
    setIsLoading(true);
    try {
      const response = await listCustomerUsers({
        search: String(search || "").trim(),
        page,
        limit,
      });
      const usersData = extractListRowsFromResponse(response, [
        "customers",
        "users",
        "userss",
      ]);

      const fetchedUsers = usersData.map((user) =>
        mapCustomerToRow(user, statesRef.current),
      );
      fetchedUsers.sort((a, b) => b.id - a.id);
      const total = parseApiPagination(response, fetchedUsers.length).total;
      setUsers(fetchedUsers);
      setTotalCount(total);
      pageCache.write(cacheKey, { items: fetchedUsers, total });
    } catch (error) {
      console.error("Error fetching User Details:", error);
      toast.error(error.response?.data?.message || "Failed to fetch customers");
      setUsers([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
      isFetching.current = false;
    }
  }, [mapCustomerToRow]);

  const fetchUsersRef = useRef(fetchUsers);
  fetchUsersRef.current = fetchUsers;

  const updateUserForm = async (updatedUser) => {
    try {
      const fields = {
        name: updatedUser.fullName || undefined,
        mobile: updatedUser.mobile || undefined,
        email: updatedUser.email || undefined,
        state: updatedUser.state || undefined,
        city: updatedUser.city || undefined,
        address: updatedUser.address || undefined,
        area: updatedUser.area || undefined,
        aadhar_no: updatedUser.aadhar_no || undefined,
        pan_no: updatedUser.pan_no || undefined,
        birth_date: normalizeDate(updatedUser.birthDate),
        marital_status: updatedUser.maritalStatus || undefined,
        anniversary_date: normalizeDate(updatedUser.anniversaryDate),
        company_name: updatedUser.companyName || undefined,
        company_address: updatedUser.companyAddress || undefined,
        gst_number: updatedUser.GST_number || undefined,
        reference: updatedUser.reference
          ? String(updatedUser.reference)
          : undefined,
        religion: updatedUser.religion || undefined,
        other_phones: updatedUser.other_phones || undefined,
        balance: toNumberOrDefault(updatedUser.balance, 0),
        gold_balance_gm:
          updatedUser.openingGoldBalance !== "" &&
          updatedUser.openingGoldBalance != null
            ? toNumberOrDefault(updatedUser.openingGoldBalance, 0)
            : undefined,
        silver_balance_gm:
          updatedUser.openingSilverBalance !== "" &&
          updatedUser.openingSilverBalance != null
            ? toNumberOrDefault(updatedUser.openingSilverBalance, 0)
            : undefined,
        status: Number(updatedUser.status) === 0 ? 0 : 1,
      };

      const hasDocFiles =
        updatedUser.aadharCardFile instanceof File ||
        updatedUser.panCardFile instanceof File ||
        updatedUser.gstDocumentFile instanceof File;

      // Omit KYC file keys to keep existing docs; only send newly picked files.
      if (updatedUser.aadharCardFile instanceof File) {
        fields.aadhar_card = updatedUser.aadharCardFile;
      }
      if (updatedUser.panCardFile instanceof File) {
        fields.pan_card = updatedUser.panCardFile;
      }
      if (updatedUser.gstDocumentFile instanceof File) {
        fields.gst_document = updatedUser.gstDocumentFile;
      }

      const response = hasDocFiles
        ? await updateCustomerUserMultipart({ id: updatedUser.id, ...fields })
        : await updateCustomerUser({ id: updatedUser.id, ...fields });

      if (!response?.success) {
        throw new Error(response?.message || "Failed to update customer");
      }
      toast.success(
        getSafeSuccessMessage(
          response?.message,
          "Customer updated successfully",
        ),
      );

      const customerRaw = extractCustomerFromPayload(response?.data ?? response);
      upsertCustomerInTable(customerRaw, updatedUser);
    } catch (error) {
      console.error("Error updating user:", error);
      toast.error(error.response?.data?.message || "Failed to update customer");
    }
  };

  // Listen for API URL changes and refetch data
  useEffect(() => {
    const handleApiUrlChange = (event) => {
      console.log(
        "API URL changed in CustomerTable, refetching customers...",
        event.detail,
      );
      setCurrentPage(1);
      fetchUsersRef.current(1, itemsPerPage, searchQuery, true);
    };

    window.addEventListener("apiUrlChanged", handleApiUrlChange);

    return () => {
      window.removeEventListener("apiUrlChanged", handleApiUrlChange);
    };
  }, [itemsPerPage, searchQuery]);

  useEffect(() => {
    const gujarat = states.find((s) => s.name === "Gujarat");
    if (gujarat) {
      setState(gujarat.name);
      setCity("Ahmedabad");
    }
  }, [states]);

  useEffect(() => {
    return () => {
      clearTimeout(referenceSearchTimerRef.current);
      clearTimeout(editReferenceSearchTimerRef.current);
    };
  }, []);

  // Server-side pagination + search (Invoice / ProductGroup pattern)
  useEffect(() => {
    const timeoutId = setTimeout(
      () => {
        fetchUsers(currentPage, itemsPerPage, searchQuery);
      },
      searchQuery ? 200 : 0,
    );
    return () => clearTimeout(timeoutId);
  }, [currentPage, itemsPerPage, searchQuery, fetchUsers]);

  // Socket.IO listener for real-time updates (other clients / tabs)
  useEffect(() => {
    const newSocket = createErpSocket();

    newSocket.on("connect_error", (error) => {
      console.error("CustomerTable: Socket connection error:", error);
    });

    const handleCustomerUpdated = (data) => {
      const customerRaw = extractCustomerFromPayload(data);
      if (!customerRaw) return;

      const updatedCustomer = mapCustomerToRow(
        customerRaw,
        statesRef.current,
      );
      setUsers((prev) =>
        prev.map((user) =>
          user.id === updatedCustomer.id ? updatedCustomer : user,
        ),
      );
    };

    const handleCustomerCreated = (data) => {
      const customerRaw = extractCustomerFromPayload(data);
      if (!customerRaw) return;

      const newCustomer = mapCustomerToRow(customerRaw, statesRef.current);
      setUsers((prev) => {
        const exists = prev.some((user) => user.id === newCustomer.id);
        if (exists) {
          return prev.map((user) =>
            user.id === newCustomer.id ? newCustomer : user,
          );
        }
        return [newCustomer, ...prev].sort((a, b) => b.id - a.id);
      });
    };

    newSocket.on("customer_updated", handleCustomerUpdated);
    newSocket.on("customer_created", handleCustomerCreated);
    newSocket.on("user_updated", handleCustomerUpdated);

    return () => {
      newSocket.off("customer_updated", handleCustomerUpdated);
      newSocket.off("customer_created", handleCustomerCreated);
      newSocket.off("user_updated", handleCustomerUpdated);
      newSocket.disconnect();
    };
  }, [mapCustomerToRow]);

  const handleEditClick = async (user) => {
    setSelectedCustomer({ ...user });
    setEditReferenceSearchInput("");
    setEditReferenceOptions([]);
    if (user.reference) {
      const option = await resolveReferenceOption(user.reference);
      setEditReferenceOption(option);
    } else {
      setEditReferenceOption(null);
    }
    // Ensure cities are loaded for this customer's state so city can be selected
    const matchedState = states.find((s) => s.name === user.state);
    if (matchedState?.iso2) {
      setSelectedState(matchedState.iso2);
    } else {
      setSelectedState("");
    }
    setShowEditModal(true);
  };

  const handleSaveChanges = () => {
    const mobileError = getMobileValidationError(selectedCustomer?.mobile, {
      required: true,
    });
    if (mobileError) {
      toast.error(mobileError);
      return;
    }

    const emailError = getEmailValidationError(selectedCustomer?.email);
    if (emailError) {
      toast.error(emailError);
      return;
    }

    if (editMobileExistsCheck.exists) {
      toast.error("Mobile number already exists");
      return;
    }

    updateUserForm({
      ...selectedCustomer,
      reference: editReferenceOption?.value
        ? String(editReferenceOption.value)
        : undefined,
    });
    setShowEditModal(false);
  };

  const handlePageChange = (newPage) => {
    const totalPages = Math.ceil(totalCount / itemsPerPage);
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  // Client-side status filter on the current server page
  const filteredUsers = users.filter((user) => {
    let matchesStatus = true;
    if (statusFilter === "Jama") {
      matchesStatus = user.balanceType === "ADVANCE";
    } else if (statusFilter === "Udhar") {
      matchesStatus = user.balanceType === "DUE";
    }
    return matchesStatus;
  });

  const displayedUsers = filteredUsers;

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();

    const mobileError = getMobileValidationError(mobile, { required: true });
    if (mobileError) {
      toast.error(mobileError);
      return;
    }

    const emailError = getEmailValidationError(email);
    if (emailError) {
      toast.error(emailError);
      return;
    }

    if (addMobileExistsCheck.exists) {
      toast.error("Mobile number already exists");
      return;
    }

    try {
      const openingBalance = toNumberOrDefault(balance, 0);
      const payload = {
        name: fullName,
        mobile,
        email: email || undefined,
        state: state || undefined,
        city: city || undefined,
        address: address || undefined,
        area: area || undefined,
        aadhar_no: aadharNo || undefined,
        pan_no: panNo || undefined,
        birth_date: normalizeDate(birthDate),
        marital_status: maritalStatus || undefined,
        anniversary_date: normalizeDate(anniversaryDate),
        company_name: companyName || undefined,
        company_address: companyAddress || undefined,
        gst_number: gstNumber || undefined,
        reference: referenceOption?.value
          ? String(referenceOption.value)
          : undefined,
        religion: religion || undefined,
        other_phones: otherPhones || undefined,
        balance: openingBalance,
        gold_balance_gm: toNumberOrDefault(openingGoldBalance, 0),
        silver_balance_gm: toNumberOrDefault(openingSilverBalance, 0),
        status: addCustomerStatus,
      };
      if (openingBalance !== 0) {
        payload.idempotency_key = createIdempotencyKey();
      }

      const hasDocFiles =
        aadharImage instanceof File ||
        panImage instanceof File ||
        gstImage instanceof File;
      if (aadharImage instanceof File) payload.aadhar_card = aadharImage;
      if (panImage instanceof File) payload.pan_card = panImage;
      if (gstImage instanceof File) payload.gst_document = gstImage;

      const response = hasDocFiles
        ? await createCustomerUserMultipart(payload)
        : await createCustomerUser(payload);
      if (!response?.success) {
        throw new Error(response?.message || "Failed to add customer");
      }
      toast.success(
        getSafeSuccessMessage(response?.message, "Customer added successfully"),
      );

      setShowAddModal(false);
      setShowSuccessModal(true);
      setTimeout(() => {
        setShowSuccessModal(false);
      }, 2000);

      // Reset form fields
      setFullName("");
      setMobile("");
      setEmail("");
      const gujarat = states.find((s) => s.name === "Gujarat");
      if (gujarat) {
        setState(gujarat.name);
        setSelectedState(gujarat.iso2);
        setCity("Ahmedabad");
      } else {
        setState("");
        setSelectedState("");
        setCity("");
      }
      setAddress("");
      setArea("");
      setAadharNo("");
      setPanNo("");
      setAadharImage(null);
      setPanImage(null);
      setGstImage(null);
      setBirthDate("");
      setMaritalStatus("");
      setAnniversaryDate("");
      setBalance("");
      setOpeningGoldBalance("");
      setOpeningSilverBalance("");
      setCompanyName("");
      setCompanyAddress("");
      setGstNumber("");
      setReligion("");
      setOtherPhones("");
      setReferenceOption(null);
      setReferenceSearchInput("");
      setReferenceOptions([]);
      setAddCustomerStatus(1);

      const customerRaw = extractCustomerFromPayload(response?.data ?? response);
      upsertCustomerInTable(customerRaw);
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error(error.response?.data?.message || "Failed to add customer");
    }
  };

  return (
    <div className="body-wrapper">
      <div className="container-fluid">
        <div
          className={`card w-100 position-relative overflow-hidden ${cardClass}`}
        >
          <div className="px-4 py-3 border-bottom d-flex justify-content-between align-items-center">
            <h4 className="card-title mb-0">Customer Table</h4>
            <div className="d-flex gap-2">
              {canSettlement && (
                <button
                  className={`btn btn-secondary ${buttonClass}`}
                  disabled={!isCurrentFinancialYear}
                  title={
                    !isCurrentFinancialYear ? FY_ADD_DISABLED_TITLE : undefined
                  }
                  onClick={() => {
                    if (!isCurrentFinancialYear) return;
                    navigate("/addsettlement");
                  }}
                >
                  Account Settlement
                </button>
              )}
              <button
                className={`btn btn-info ${buttonClass}`}
                onClick={() => navigate("/customerledger")}
              >
                Ledger
              </button>
              {canAddCustomer && (
                <button
                  className={`btn btn-success ${buttonClass}`}
                  onClick={() => {
                    setShowAddModal(true);
                    setAddCustomerStatus(1);
                    const gujarat = states.find((s) => s.name === "Gujarat");
                    if (gujarat) {
                      setSelectedState(gujarat.iso2);
                      setState(gujarat.name);
                      setCity("Ahmedabad");
                    }
                  }}
                >
                  Add Customer
                </button>
              )}
            </div>
          </div>

          <div className="card-body p-4">
            <div className="d-flex gap-3 mb-3 align-items-center">
              <input
                type="text"
                className={`form-control ${buttonClass}`}
                style={{ maxWidth: "260px" }}
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
              />
              <select
                className={`form-select w-auto ms-auto ${buttonClass}`}
                style={{ maxWidth: "200px" }}
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="All">All</option>
                <option value="Jama">Jama</option>
                <option value="Udhar">Udhar</option>
              </select>
            </div>

            <div className={`table-responsive mb-4 border ${cardClass}`}>
              <table
                className={`table text-nowrap mb-0 align-middle ${tableClass}`}
              >
                <thead className="text-dark fs-4">
                  <tr>
                    <th>Name</th>
                    <th>Mobile</th>
                    <th>City</th>
                    <th>Balance Type</th>
                    <th>Ledger Balance</th>
                    <th>Status</th>
                    {canEditCustomer && <th>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td colSpan={canEditCustomer ? 7 : 6} className="text-center">
                        Loading...
                      </td>
                    </tr>
                  ) : displayedUsers.length > 0 ? (
                    displayedUsers.map((user) => (
                      <tr key={user.id}>
                        <td>
                          <div>{user.fullName}</div>
                          {user.email && (
                            <div className="small text-muted">{user.email}</div>
                          )}
                        </td>
                        <td>{user.mobile}</td>
                        <td>{user.city}</td>
                        <td>
                          <span
                            className={`badge ${user.balanceType === "ADVANCE"
                                ? "bg-success-subtle text-success"
                                : "bg-danger-subtle text-danger"
                              }`}
                          >
                            {user.balanceType}
                          </span>
                        </td>
                        <td>
                          <span
                            className={`badge ${user.balanceType === "ADVANCE"
                                ? "bg-success-subtle text-success"
                                : "bg-danger-subtle text-danger"
                              }`}
                          >
                            {formatIndianRupee(
                              toNumberOrDefault(user.finalBalance, 0),
                            )}
                          </span>
                        </td>
                        <td>
                          <span
                            className={`badge ${Number(user.status) === 1
                                ? "bg-success-subtle text-success"
                                : "bg-danger-subtle text-danger"
                              }`}
                          >
                            {Number(user.status) === 1 ? "Active" : "Inactive"}
                          </span>
                        </td>
                        {canEditCustomer && (
                          <td>
                            <button
                              className={`btn btn-primary btn-sm ${buttonClass}`}
                              onClick={() => handleEditClick(user)}
                              title="Edit Customer"
                            >
                              <Icon
                                icon="solar:pen-bold"
                                width="16"
                                height="16"
                              />
                            </button>
                          </td>
                        )}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={canEditCustomer ? 7 : 6} className="text-center">
                        No customers found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <Pagination
              totalItems={totalCount}
              itemsPerPage={itemsPerPage}
              currentPage={currentPage}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
              itemsPerPageOptions={[10, 20, 30, 50, 100]}
            />
          </div>
        </div>
      </div>

      {/* Add Customer Modal */}
      <Modal
        show={showAddModal}
        onHide={() => {
          setShowAddModal(false);
          setAadharImage(null);
          setPanImage(null);
          setGstImage(null);
        }}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Add Customer</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleAddSubmit}>
            {/* Personal Details */}

            <Row className="gy-3 mb-3">
              <Col xs={12} sm={6} lg={4}>
                <Form.Label>Full Name </Form.Label>
                <Form.Control
                  type="text"
                  required
                  placeholder="Enter Full Name"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                />
              </Col>
              <Col xs={12} sm={6} lg={4}>
                <Form.Label>Mobile No </Form.Label>
                <Form.Control
                  {...mobileInputFieldProps}
                  required
                  placeholder="Enter Mobile No"
                  value={mobile}
                  onChange={(e) => setMobile(sanitizeMobileInput(e.target.value))}
                  isInvalid={addMobileExistsCheck.exists}
                />
                <MobileExistsFeedback
                  exists={addMobileExistsCheck.exists}
                  checking={addMobileExistsCheck.checking}
                />
              </Col>
              <Col xs={12} sm={6} lg={4}>
                <Form.Label>Email</Form.Label>
                <Form.Control
                  {...emailInputFieldProps}
                  placeholder="Enter Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </Col>
            </Row>

            <Row className="gy-3 mb-3">
              <Col xs={12} sm={6} lg={4}>
                <Form.Group controlId="stateSelect">
                  <Form.Label>State </Form.Label>
                  <Select
                    options={stateOptions}
                    value={
                      selectedState
                        ? { label: state, value: selectedState }
                        : null
                    }
                    onChange={(option) => {
                      setSelectedState(option.value);
                      setState(option.label);
                      setCity(""); // Reset city when state changes
                    }}
                    styles={customSelectStyles}
                    placeholder="Select State"
                  />
                </Form.Group>
              </Col>
              <Col xs={12} sm={6} lg={4}>
                <Form.Group controlId="citySelect">
                  <Form.Label>City </Form.Label>
                  <Select
                    options={cityOptions}
                    value={city ? { label: city, value: city } : null}
                    onChange={(option) => setCity(option.value)}
                    isDisabled={!selectedState}
                    styles={customSelectStyles}
                    placeholder="Select City"
                  />
                </Form.Group>
              </Col>
              <Col xs={12} sm={6} lg={4}>
                <Form.Label>Address </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  required
                  placeholder="Enter Address"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                />
              </Col>
            </Row>
            <Row className="gy-3 mb-3">
              <Col xs={12} sm={6} lg={4}>
                <Form.Label>Area</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter Area"
                  value={area}
                  onChange={(e) => setArea(e.target.value)}
                />
              </Col>
              <Col xs={12} sm={6} lg={4}>
                <DocNumberInput
                  label="Aadhar No"
                  controlId="addAadharNo"
                  placeholder="Enter Aadhar No"
                  value={aadharNo}
                  onChange={(e) => setAadharNo(e.target.value)}
                  file={aadharImage}
                  onFileChange={setAadharImage}
                />
              </Col>
              <Col xs={12} sm={6} lg={4}>
                <DocNumberInput
                  label="PAN No"
                  controlId="addPanNo"
                  placeholder="Enter PAN No"
                  value={panNo}
                  onChange={(e) => setPanNo(e.target.value)}
                  file={panImage}
                  onFileChange={setPanImage}
                />
              </Col>
            </Row>

            <Row className="gy-3 mb-3">
              <Col xs={12} sm={6} lg={4}>
                <Form.Label>Birth Date</Form.Label>
                <Form.Control
                  type="date"
                  value={birthDate || ""}
                  onChange={(e) => setBirthDate(e.target.value || "")}
                />
              </Col>
              <Col xs={12} sm={6} lg={4}>
                <Form.Label>Marital Status</Form.Label>
                <div className="d-flex gap-3">
                  <Form.Check
                    type="radio"
                    label="Married"
                    name="maritalStatus"
                    value="married"
                    checked={maritalStatus === "married"}
                    onChange={(e) => setMaritalStatus(e.target.value)}
                  />
                  <Form.Check
                    type="radio"
                    label="Unmarried"
                    name="maritalStatus"
                    value="unmarried"
                    checked={maritalStatus === "unmarried"}
                    onChange={(e) => setMaritalStatus(e.target.value)}
                  />
                </div>
              </Col>
              <Col xs={12} sm={6} lg={4}>
                <Form.Label>Anniversary Date</Form.Label>
                <Form.Control
                  type="date"
                  value={anniversaryDate || ""}
                  onChange={(e) => setAnniversaryDate(e.target.value || "")}
                />
              </Col>
            </Row>

            <Row className="gy-3 mb-3">
              <Col xs={12} sm={6} lg={4}>
                <Form.Label>Company Name</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter Company Name"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                />
              </Col>
              <Col xs={12} sm={6} lg={4}>
                <Form.Label>Company Address</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  placeholder="Enter Company Address"
                  value={companyAddress}
                  onChange={(e) => setCompanyAddress(e.target.value)}
                />
              </Col>
              <Col xs={12} sm={6} lg={4}>
                <DocNumberInput
                  label="GST Number"
                  controlId="addGstNumber"
                  placeholder="Enter GST Number"
                  value={gstNumber}
                  onChange={(e) => setGstNumber(e.target.value)}
                  file={gstImage}
                  onFileChange={setGstImage}
                />
              </Col>
            </Row>

            <Row className="gy-3 mb-3">
              <Col xs={12} sm={6} lg={4}>
                <Form.Label>Reference</Form.Label>
                <Select
                  options={referenceOptions}
                  value={referenceOption}
                  inputValue={referenceSearchInput}
                  onInputChange={(value, actionMeta) => {
                    if (actionMeta.action === "input-change") {
                      scheduleReferenceSearch(value, false);
                    }
                  }}
                  onChange={(option) => {
                    setReferenceOption(option);
                    setReferenceSearchInput("");
                    setReferenceOptions([]);
                  }}
                  filterOption={() => true}
                  isLoading={loadingReferenceSearch}
                  isClearable
                  styles={customSelectStyles}
                  menuPortalTarget={
                    typeof document !== "undefined" ? document.body : null
                  }
                  menuPosition="fixed"
                  placeholder="Search by name or mobile"
                  noOptionsMessage={() =>
                    referenceSearchInput.trim()
                      ? "No customers found"
                      : "Type name or mobile to search"
                  }
                  {...customerSelectMenuProps}
                />
              </Col>
              <Col xs={12} sm={6} lg={4}>
                <Form.Label>Other Contact Details</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter other phone numbers"
                  value={otherPhones}
                  onChange={(e) => setOtherPhones(e.target.value)}
                />
              </Col>
              <Col xs={12} sm={6} lg={4}>
                <Form.Label>Religion/Gotra/Caste</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter Religion/Gotra/Caste"
                  value={religion}
                  onChange={(e) => setReligion(e.target.value)}
                />
              </Col>
            </Row>

            <Row className="gy-3 mb-3">
              <Col xs={12} sm={6} lg={4}>
                <Form.Label>Opening Balance</Form.Label>
                <Form.Control
                  type="number"
                  placeholder="Enter Opening Balance"
                  value={balance}
                  onChange={(e) => setBalance(e.target.value)}
                />
              </Col>
              <Col xs={12} sm={6} lg={4}>
                <Form.Label>Opening Gold Balance</Form.Label>
                <Form.Control
                  type="number"
                  step="0.001"
                  placeholder="Enter Opening Gold Balance"
                  value={openingGoldBalance}
                  onChange={(e) => setOpeningGoldBalance(e.target.value)}
                />
              </Col>
              <Col xs={12} sm={6} lg={4}>
                <Form.Label>Opening Silver Balance</Form.Label>
                <Form.Control
                  type="number"
                  step="0.001"
                  placeholder="Enter Opening Silver Balance"
                  value={openingSilverBalance}
                  onChange={(e) => setOpeningSilverBalance(e.target.value)}
                />
              </Col>
            </Row>

            <Row className="gy-3 mb-3">
              <Col xs={12} sm={6} lg={4}>
                <Form.Label>Status</Form.Label>
                <div className="d-flex gap-3 flex-wrap">
                  <Form.Check
                    type="radio"
                    id="add-status-active"
                    label="Active"
                    name="addCustomerAccountStatus"
                    checked={addCustomerStatus === 1}
                    onChange={() => setAddCustomerStatus(1)}
                  />
                  <Form.Check
                    type="radio"
                    id="add-status-inactive"
                    label="Inactive"
                    name="addCustomerAccountStatus"
                    checked={addCustomerStatus === 0}
                    onChange={() => setAddCustomerStatus(0)}
                  />
                </div>
              </Col>
            </Row>

            <div className="d-flex flex-wrap gap-3 justify-content-end">
              <Button
                type="submit"
                variant="primary"
                className={buttonClass}
                disabled={
                  addMobileExistsCheck.exists || addMobileExistsCheck.checking
                }
              >
                Submit
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Success Modal */}

      {/* Edit Modal */}
      <Modal
        show={showEditModal}
        onHide={() => {
          setShowEditModal(false);
          setSelectedCustomer((prev) =>
            prev
              ? {
                  ...prev,
                  aadharCardFile: null,
                  panCardFile: null,
                  gstDocumentFile: null,
                }
              : prev,
          );
        }}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit Customer</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedCustomer && (
            <Form>
              <Row className="gy-3 mb-3">
                <Col xs={12} sm={6} lg={4}>
                  <Form.Label>Full Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={selectedCustomer.fullName}
                    onChange={(e) =>
                      setSelectedCustomer({
                        ...selectedCustomer,
                        fullName: e.target.value,
                      })
                    }
                  />
                </Col>
                <Col xs={12} sm={6} lg={4}>
                  <Form.Label>Mobile</Form.Label>
                  <Form.Control
                    {...mobileInputFieldProps}
                    value={selectedCustomer.mobile}
                    onChange={(e) =>
                      setSelectedCustomer({
                        ...selectedCustomer,
                        mobile: sanitizeMobileInput(e.target.value),
                      })
                    }
                    isInvalid={editMobileExistsCheck.exists}
                  />
                  <MobileExistsFeedback
                    exists={editMobileExistsCheck.exists}
                    checking={editMobileExistsCheck.checking}
                  />
                </Col>
                <Col xs={12} sm={6} lg={4}>
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    {...emailInputFieldProps}
                    value={selectedCustomer.email}
                    onChange={(e) =>
                      setSelectedCustomer({
                        ...selectedCustomer,
                        email: e.target.value,
                      })
                    }
                  />
                </Col>
              </Row>

              <Row className="gy-3 mb-3">
                <Col xs={12} sm={6} lg={4}>
                  <Form.Group controlId="editStateSelect">
                    <Form.Label>State</Form.Label>
                    <Select
                      options={stateOptions}
                      value={
                        selectedCustomer.state
                          ? {
                            label: selectedCustomer.state,
                            value: selectedState,
                          }
                          : null
                      }
                      onChange={(option) => {
                        setSelectedState(option.value);
                        setSelectedCustomer({
                          ...selectedCustomer,
                          state: option.label,
                          city: "", // Reset city when state changes
                        });
                      }}
                      styles={customSelectStyles}
                      placeholder="Select State"
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} sm={6} lg={4}>
                  <Form.Group controlId="editCitySelect">
                    <Form.Label>City</Form.Label>
                    <Select
                      options={cityOptions}
                      value={
                        selectedCustomer.city
                          ? {
                            label: selectedCustomer.city,
                            value: selectedCustomer.city,
                          }
                          : null
                      }
                      onChange={(option) =>
                        setSelectedCustomer({
                          ...selectedCustomer,
                          city: option.value,
                        })
                      }
                      isDisabled={!selectedCustomer?.state}
                      styles={customSelectStyles}
                      placeholder="Select City"
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} sm={6} lg={4}>
                  <Form.Label>Address</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder="Enter Address"
                    value={selectedCustomer.address || ""}
                    onChange={(e) =>
                      setSelectedCustomer({
                        ...selectedCustomer,
                        address: e.target.value,
                      })
                    }
                  />
                </Col>
              </Row>
              <Row className="gy-3 mb-3">
                <Col xs={12} sm={6} lg={4}>
                  <Form.Label>Area</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter Area"
                    value={selectedCustomer.area || ""}
                    onChange={(e) =>
                      setSelectedCustomer({
                        ...selectedCustomer,
                        area: e.target.value,
                      })
                    }
                  />
                </Col>
                <Col xs={12} sm={6} lg={4}>
                  <DocNumberInput
                    label="Aadhar No"
                    controlId="editAadharNo"
                    placeholder="Enter Aadhar No"
                    value={selectedCustomer.aadhar_no || ""}
                    onChange={(e) =>
                      setSelectedCustomer({
                        ...selectedCustomer,
                        aadhar_no: e.target.value,
                      })
                    }
                    file={selectedCustomer.aadharCardFile || null}
                    existingUrl={selectedCustomer.aadhar_card || ""}
                    onFileChange={(file) =>
                      setSelectedCustomer({
                        ...selectedCustomer,
                        aadharCardFile: file,
                      })
                    }
                  />
                </Col>
                <Col xs={12} sm={6} lg={4}>
                  <DocNumberInput
                    label="PAN No"
                    controlId="editPanNo"
                    placeholder="Enter PAN No"
                    value={selectedCustomer.pan_no || ""}
                    onChange={(e) =>
                      setSelectedCustomer({
                        ...selectedCustomer,
                        pan_no: e.target.value,
                      })
                    }
                    file={selectedCustomer.panCardFile || null}
                    existingUrl={selectedCustomer.pan_card || ""}
                    onFileChange={(file) =>
                      setSelectedCustomer({
                        ...selectedCustomer,
                        panCardFile: file,
                      })
                    }
                  />
                </Col>
              </Row>

              <Row className="gy-3 mb-3">
                <Col xs={12} sm={6} lg={4}>
                  <Form.Label>Birth Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={
                      selectedCustomer.birthDate &&
                        selectedCustomer.birthDate !== "0000-00-00"
                        ? selectedCustomer.birthDate
                        : ""
                    }
                    onChange={(e) =>
                      setSelectedCustomer({
                        ...selectedCustomer,
                        birthDate: e.target.value || "",
                      })
                    }
                  />
                </Col>
                <Col xs={12} sm={6} lg={4}>
                  <Form.Label>Marital Status</Form.Label>
                  <div className="d-flex gap-3">
                    <Form.Check
                      type="radio"
                      label="Married"
                      name="maritalStatus"
                      value="married"
                      checked={selectedCustomer.maritalStatus === "married"}
                      onChange={(e) =>
                        setSelectedCustomer({
                          ...selectedCustomer,
                          maritalStatus: e.target.value,
                        })
                      }
                    />
                    <Form.Check
                      type="radio"
                      label="Unmarried"
                      name="maritalStatus"
                      value="unmarried"
                      checked={selectedCustomer.maritalStatus === "unmarried"}
                      onChange={(e) =>
                        setSelectedCustomer({
                          ...selectedCustomer,
                          maritalStatus: e.target.value,
                          anniversaryDate: "", // Clear anniversary date when unmarried
                        })
                      }
                    />
                  </div>
                </Col>
                <Col xs={12} sm={6} lg={4}>
                  <Form.Label>Anniversary Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={
                      selectedCustomer.anniversaryDate &&
                        selectedCustomer.anniversaryDate !== "0000-00-00"
                        ? selectedCustomer.anniversaryDate
                        : ""
                    }
                    onChange={(e) =>
                      setSelectedCustomer({
                        ...selectedCustomer,
                        anniversaryDate: e.target.value || "",
                      })
                    }
                  />
                </Col>
              </Row>

              <Row className="gy-3 mb-3">
                <Col xs={12} sm={6} lg={4}>
                  <Form.Label>Company Name</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter Company Name"
                    value={selectedCustomer.companyName || ""}
                    onChange={(e) =>
                      setSelectedCustomer({
                        ...selectedCustomer,
                        companyName: e.target.value,
                      })
                    }
                  />
                </Col>
                <Col xs={12} sm={6} lg={4}>
                  <Form.Label>Company Address</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder="Enter Company Address"
                    value={selectedCustomer.companyAddress || ""}
                    onChange={(e) =>
                      setSelectedCustomer({
                        ...selectedCustomer,
                        companyAddress: e.target.value,
                      })
                    }
                  />
                </Col>
                <Col xs={12} sm={6} lg={4}>
                  <DocNumberInput
                    label="GST Number"
                    controlId="editGstNumber"
                    placeholder="Enter GST Number"
                    value={selectedCustomer.GST_number || ""}
                    onChange={(e) =>
                      setSelectedCustomer({
                        ...selectedCustomer,
                        GST_number: e.target.value,
                      })
                    }
                    file={selectedCustomer.gstDocumentFile || null}
                    existingUrl={selectedCustomer.gst_document || ""}
                    onFileChange={(file) =>
                      setSelectedCustomer({
                        ...selectedCustomer,
                        gstDocumentFile: file,
                      })
                    }
                  />
                </Col>
              </Row>

              <Row className="gy-3 mb-3">
                <Col xs={12} sm={6} lg={4}>
                  <Form.Label>Reference</Form.Label>
                  <Select
                    options={editReferenceOptions}
                    value={editReferenceOption}
                    inputValue={editReferenceSearchInput}
                    onInputChange={(value, actionMeta) => {
                      if (actionMeta.action === "input-change") {
                        scheduleReferenceSearch(value, true);
                      }
                    }}
                    onChange={(option) => {
                      setEditReferenceOption(option);
                      setEditReferenceSearchInput("");
                      setEditReferenceOptions([]);
                    }}
                    filterOption={() => true}
                    isLoading={loadingEditReferenceSearch}
                    isClearable
                    styles={customSelectStyles}
                    menuPortalTarget={
                      typeof document !== "undefined" ? document.body : null
                    }
                    menuPosition="fixed"
                    placeholder="Search by name or mobile"
                    noOptionsMessage={() =>
                      editReferenceSearchInput.trim()
                        ? "No customers found"
                        : "Type name or mobile to search"
                    }
                    {...customerSelectMenuProps}
                  />
                </Col>
                <Col xs={12} sm={6} lg={4}>
                  <Form.Label>Other Contact Details</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter other phone numbers"
                    value={selectedCustomer.other_phones || ""}
                    onChange={(e) =>
                      setSelectedCustomer({
                        ...selectedCustomer,
                        other_phones: e.target.value,
                      })
                    }
                  />
                </Col>
                <Col xs={12} sm={6} lg={4}>
                  <Form.Label>Religion/Gotra/Caste</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter Religion/Gotra/Caste"
                    value={selectedCustomer.religion || ""}
                    onChange={(e) =>
                      setSelectedCustomer({
                        ...selectedCustomer,
                        religion: e.target.value,
                      })
                    }
                  />
                </Col>
              </Row>

              <Row className="gy-3 mb-3">
                <Col xs={12} sm={6} lg={4}>
                  <Form.Label>Opening Balance</Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="Enter Opening Balance"
                    value={selectedCustomer.balance ?? ""}
                    onChange={(e) =>
                      setSelectedCustomer({
                        ...selectedCustomer,
                        balance: e.target.value,
                      })
                    }
                  />
                </Col>
                <Col xs={12} sm={6} lg={4}>
                  <Form.Label>Opening Gold Balance</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.001"
                    placeholder="Enter Opening Gold Balance"
                    value={selectedCustomer.openingGoldBalance ?? ""}
                    onChange={(e) =>
                      setSelectedCustomer({
                        ...selectedCustomer,
                        openingGoldBalance: e.target.value,
                      })
                    }
                  />
                </Col>
                <Col xs={12} sm={6} lg={4}>
                  <Form.Label>Opening Silver Balance</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.001"
                    placeholder="Enter Opening Silver Balance"
                    value={selectedCustomer.openingSilverBalance ?? ""}
                    onChange={(e) =>
                      setSelectedCustomer({
                        ...selectedCustomer,
                        openingSilverBalance: e.target.value,
                      })
                    }
                  />
                </Col>
              </Row>

              <Row className="gy-3 mb-3">
                <Col xs={12} sm={6} lg={4}>
                  <Form.Label>Status</Form.Label>
                  <div className="d-flex gap-3 flex-wrap">
                    <Form.Check
                      type="radio"
                      id="edit-status-active"
                      label="Active"
                      name="editCustomerAccountStatus"
                      checked={Number(selectedCustomer.status) === 1}
                      onChange={() =>
                        setSelectedCustomer({
                          ...selectedCustomer,
                          status: 1,
                        })
                      }
                    />
                    <Form.Check
                      type="radio"
                      id="edit-status-inactive"
                      label="Inactive"
                      name="editCustomerAccountStatus"
                      checked={Number(selectedCustomer.status) === 0}
                      onChange={() =>
                        setSelectedCustomer({
                          ...selectedCustomer,
                          status: 0,
                        })
                      }
                    />
                  </div>
                </Col>
              </Row>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="primary"
            className={buttonClass}
            onClick={handleSaveChanges}
            disabled={
              editMobileExistsCheck.exists || editMobileExistsCheck.checking
            }
          >
            Save
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default CustomerTable;
